import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { IconPlus, IconShoppingCart, IconUpload } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import ImportDialog from '../../../../../ui-component/extended/ImportDialog';
import PaginationBar from '../../../../../ui-component/extended/PaginationBar';
import LoadingBox from '../../../../../ui-component/extended/LoadingBox';
import useRequestGuard from '../../../../../hooks/useRequestGuard';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    confirmed: { label: 'Confirmado', color: 'success' },
    cancelled: { label: 'Cancelado', color: 'error' }
};

const FULFILLMENT_STATUS_LABEL = {
    reserved: { label: 'Estoque reservado', color: 'default' },
    picking: { label: 'Em separação', color: 'warning' },
    shipped: { label: 'Expedido', color: 'success' }
};

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const ViewAllSalesOrders = () => {
    const history = useHistory();
    const { start, isCurrent } = useRequestGuard();

    const [appId, setAppId] = useState(null);
    const [orders, setOrders] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ count: 0, numPages: 1 });
    const [loading, setLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarPedidos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, statusFilter, page]);

    const listarPedidos = async () => {
        const requestId = start();
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('page', page);
            if (statusFilter) params.set('status', statusFilter);
            const res = await api.get(`v1/apps/${appId}/sales_orders/?${params.toString()}`);
            if (!isCurrent(requestId)) return;
            setOrders(res.data.orders || []);
            setPagination({ count: res.data.count || 0, numPages: res.data.num_pages || 1 });
        } finally {
            if (isCurrent(requestId)) setLoading(false);
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconShoppingCart}
                gradient="teal"
                title="Vendas"
                subtitle="Pedidos de venda"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            select
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            sx={{ width: 180 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="draft">Rascunho</MenuItem>
                            <MenuItem value="confirmed">Confirmado</MenuItem>
                            <MenuItem value="cancelled">Cancelado</MenuItem>
                        </TextField>

                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={() => history.push('/dashboard/sales/new')}
                        >
                            Novo Pedido
                        </Button>
                        <Button variant="outlined" startIcon={<IconUpload size={18} />} onClick={() => setImportDialogOpen(true)}>
                            Importar
                        </Button>
                    </Stack>
                }
            />

            {loading && <LoadingBox label="Carregando pedidos..." />}

            {!loading && (
            <Grid container spacing={2}>
                {orders.map((order) => {
                    const statusInfo = STATUS_LABEL[order.status] || { label: order.status, color: 'default' };
                    const fulfillmentInfo = order.fulfillment_status ? FULFILLMENT_STATUS_LABEL[order.fulfillment_status] : null;
                    return (
                        <Grid item xs={12} key={order.id}>
                            <Card sx={{ cursor: 'pointer' }} onClick={() => history.push(`/dashboard/sales/${order.id}/`)}>
                                <CardContent>
                                    <Grid container alignItems="center" spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Typography fontWeight={600}>{order.organization_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {order.order_date}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Typography>{formatCurrencyBR(order.total_amount)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            {fulfillmentInfo && (
                                                <Chip label={fulfillmentInfo.label} color={fulfillmentInfo.color} size="small" variant="outlined" />
                                            )}
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
            )}

            {!loading && orders.length === 0 && (
                <Box mt={10} textAlign="center" color="text.secondary">
                    <Typography variant="h6">Nenhum pedido encontrado</Typography>
                </Box>
            )}

            <PaginationBar page={page} numPages={pagination.numPages} count={pagination.count} onChange={setPage} />

            <ImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                title="Importar Pedidos de Venda"
                entityLabel="itens de pedido"
                systemFields={[
                    { key: 'referencia_pedido', label: 'Referência do Pedido (agrupa linhas no mesmo pedido)', required: false },
                    { key: 'cliente_cnpj', label: 'CNPJ do Cliente (ou use nome)', required: false },
                    { key: 'cliente_nome', label: 'Nome do Cliente (se não usar CNPJ)', required: false },
                    { key: 'sku_produto', label: 'SKU do Produto (ou use nome+variação)', required: false },
                    { key: 'nome_produto', label: 'Nome do Produto (se não usar SKU)', required: false },
                    { key: 'nome_variacao', label: 'Nome da Variação (se não usar SKU)', required: false },
                    { key: 'codigo_servico', label: 'Código do Serviço (se o item for serviço)', required: false },
                    { key: 'quantidade', label: 'Quantidade', required: true },
                    { key: 'valor_unitario', label: 'Valor Unitário (vazio usa tabela de preço/preço de venda)', required: false },
                    { key: 'deposito', label: 'Depósito (opcional, precisa já existir)', required: false },
                    { key: 'data_pedido', label: 'Data do Pedido (AAAA-MM-DD)', required: false }
                ]}
                fieldsEndpoint={`v1/apps/${appId}/sales_order_fields/`}
                importUrl={`v1/apps/${appId}/sales_orders/import/`}
                onImported={listarPedidos}
            />
        </Box>
    );
};

export default ViewAllSalesOrders;
